import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  useTheme,
} from '@mui/material'
import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import ComponentDisplay from 'components/ComponentDisplay'
import type { ChangeEvent, FC, JSX } from 'react'
import { startTransition } from 'react'
import { useNavigate } from 'react-router-dom'
import type { PaginationInfo } from 'types/common'
import { ComponentSourceType, getCompositeFunction } from 'types/components'
import type { KeyedObject } from 'types/root'

interface TargetsTableProps {
  periodLabelMap: Record<string, string>
  organizationLabelMap: Record<string, string>
  targets: any
  page: number
  rowsPerPage: number
  sort: string | null
  order: string
  handleChangePage: (event: unknown, newPage: number) => void
  handleChangeRowsPerPage: (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => void
  handleSort: (property: string) => void
}

const TargetsTable: FC<TargetsTableProps> = ({
  periodLabelMap,
  organizationLabelMap,
  targets,
  page,
  rowsPerPage,
  sort,
  order,
  handleChangePage,
  handleChangeRowsPerPage,
  handleSort,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()

  // table columns
  interface ColumnProps {
    id: string
    label: string
    minWidth: number
    align?: 'right' | 'left' | 'inherit' | 'center' | 'justify' | undefined
    format?: (row: KeyedObject) => string | boolean | JSX.Element
  }

  const columns: ColumnProps[] = [
    {
      id: 'organizationId',
      label: 'Team',
      minWidth: 170,
      format: (row: KeyedObject) => organizationLabelMap[row.organizationId],
    },
    { id: 'periodId', label: 'Period', minWidth: 170, format: (row: KeyedObject) => periodLabelMap[row.periodId] },
    {
      id: 'label',
      label: 'Name',
      minWidth: 170,
      format: (row: KeyedObject) => {
        let displayLabel = row.label
        // Only show user info for employee-level components
        if (!row.isOrganizationLevel) {
          if (row.userId && row.userId !== '00000000-0000-0000-0000-000000000000') {
            // Real user: append user name if available
            if (row.userFirstName || row.userLastName) {
              const userName = [row.userFirstName, row.userLastName].filter(Boolean).join(' ')
              displayLabel = `${row.label} (${userName})`
            }
          } else {
            // Employee default entry (userId is null or uuid.Nil): append "(default)"
            displayLabel = `${row.label} (default)`
          }
        }
        return displayLabel
      },
    },
    {
      id: 'value',
      label: 'Value',
      minWidth: 170,
      format: (row: KeyedObject) => (
        <ComponentDisplay
          componentType={row.componentType}
          value={row.value}
          sourceType={
            row.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
              ? ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
              : ComponentSourceType.COMPONENT_SOURCE_MANUAL
          }
          compositeFunction={getCompositeFunction(row.sourceInfo)}
          componentLabel={row.label}
          periodLabel={periodLabelMap[row.periodId]}
          numericDecimalPlaces={row.numericDecimalPlaces}
          fontSize="14px"
          isTarget={true}
        />
      ),
    },
    {
      id: 'id',
      label: 'Actions',
      minWidth: 50,
      format: (row: KeyedObject) => (
        <Box display="flex">
          <Box mx={0}>
            <Tooltip title="Edit">
              <IconButton
                color="primary"
                onClick={() =>
                  startTransition(() => {
                    navigate(
                      `/admin/targets/edit?componentId=${row.componentId}&organizationId=${row.organizationId}&periodId=${row.periodId}&userId=${row.userId || 'null'}`
                    )
                  })
                }
              >
                <EditOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ),
    },
  ]

  const paginationInfo: PaginationInfo = targets?.paginationInfo ?? {
    currentPage: 0,
    perPage: 10,
    totalItems: 0,
    totalPages: 0,
  }

  return (
    <Paper sx={{ mb: 3 }}>
      {/* table */}
      <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', minHeight: '300px' }}>
        <Table stickyHeader size="small" aria-label="sticky table">
          <TableHead
            sx={{
              '& th': {
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `2px solid ${theme.palette.divider} !important`,
              },
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  id={`cell-col-${column.id || ''}`}
                  sx={{
                    minWidth: column.minWidth,
                    position: 'sticky !important',
                  }}
                  key={column.id}
                  align={column.align}
                >
                  {column.id === 'id' ? (
                    column.label
                  ) : (
                    <TableSortLabel
                      active={sort === column.id}
                      direction={sort === column.id ? (order as 'asc' | 'desc') : 'asc'}
                      onClick={() => handleSort(column.id as string)}
                    >
                      {column.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {targets && targets.targets && targets.targets.length > 0 ? (
              targets.targets.map((row: KeyedObject, index: number) => (
                <TableRow
                  id={`table-row-${row.id || index}`}
                  sx={{ py: 3 }}
                  role="checkbox"
                  tabIndex={-1}
                  key={`table-row-${row.id || index}`}
                >
                  {columns.map((column) => {
                    const value = row[column.id]
                    return (
                      <TableCell id={`cell-${column.id}`} key={`cell-${column.id}`} align={column.align}>
                        {column.format ? column.format(row) : value}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" sx={{ px: '20px' }}>
        <AnimateButton>
          <Tooltip title="Add Target">
            <Button
              variant="contained"
              startIcon={<PlusOutlined />}
              color="primary"
              onClick={() =>
                startTransition(() => {
                  navigate('/admin/components/targets/create')
                })
              }
            >
              Target
            </Button>
          </Tooltip>
        </AnimateButton>

        {/* table pagination */}
        {targets && targets.targets && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 100, 200]}
            component="div"
            count={paginationInfo.totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Box>
    </Paper>
  )
}

export default TargetsTable
